import { useCallback, useState } from "react"

import { clsx } from "clsx"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import type {
  ShareReadinessDto,
  ShareStateDto,
} from "~/api/dto/presentation-share.dto"
import { useCurrentUser } from "~/api/hooks/useCurrentUser"
import { Drawer } from "~/components/ui-components/v2"

import { ActivityList } from "./ActivityList"
import type { NotReadyReadiness, ShareKindBundle } from "./share-kind-bundle"

export const SHARE_TITLE = `Share`
export const SHARE_DESCRIPTION = `Publish a link the homeowner can open without signing in. It stays available for 30 days unless you unpublish it.`
export const PUBLISH_LABEL = `Publish link`
export const PUBLISHING_LABEL = `Publishing`
export const UNPUBLISH_LABEL = `Unpublish`
export const UNPUBLISHING_LABEL = `Unpublishing`
export const COPY_LINK_LABEL = `Copy link`
export const LINK_COPIED_LABEL = `Copied`
export const OPEN_PRESENTATION_LABEL = `Open presentation`
export const PUBLIC_LINK_LABEL = `Public presentation link`
export const LOADING_SHARE_LABEL = `Loading share status`
export const SHARE_LOAD_ERROR_PROMPT = `Unable to load the share status. Close and try again.`
export const PUBLISH_ERROR_PROMPT = `Unable to publish the link`
export const UNPUBLISH_ERROR_PROMPT = `Unable to unpublish the link`
export const COPY_ERROR_PROMPT = `Unable to copy the link. Select the address and copy it manually.`
export const VIEW_ONLY_NOTE = `You have view-only access to this proposal.`

const formatExpiration = (expiresAt: string): string | null => {
  const expires = new Date(expiresAt)
  if (Number.isNaN(expires.getTime())) {
    return null
  }

  return new Intl.DateTimeFormat(`en-US`, {
    month: `long`,
    day: `numeric`,
    year: `numeric`,
  }).format(expires)
}

/**
 * The routed Share drawer every Proposal Kind opens from its Setup rail
 * (`/:kind-proposal/:proposalId/share`). Every lifecycle answer comes from the
 * server's ShareState, re-read on each open: readiness decides whether Publish
 * is offered, and `grant.state` decides whether a link is presented. The
 * kind's bundle supplies the reads, the permission mirror, and the not-ready
 * explanation with its fix links.
 */
export function ShareDrawer<Readiness extends ShareReadinessDto>({
  bundle,
}: {
  bundle: ShareKindBundle<Readiness>
}): JSX.Element | null {
  const { proposalId } = useParams<{ proposalId: string }>()
  const navigate = useNavigate()
  const { state } = useLocation()

  const onClose = useCallback(() => {
    if (proposalId) {
      navigate(bundle.returnTo(proposalId, state))
    }
  }, [bundle, navigate, proposalId, state])

  if (!proposalId) {
    return null
  }

  return (
    <Drawer
      open
      theme="dark"
      onClose={onClose}
      title={SHARE_TITLE}
      widthClass="sm:max-w-lg"
    >
      <ShareContent bundle={bundle} proposalId={proposalId} />
    </Drawer>
  )
}

function ShareContent<Readiness extends ShareReadinessDto>({
  bundle,
  proposalId,
}: {
  bundle: ShareKindBundle<Readiness>
  proposalId: string
}): JSX.Element {
  const share = bundle.useShareState(proposalId)
  const views = bundle.useShareViews(proposalId)
  const { ExtraActions } = bundle

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[length:var(--step--1)] text-bp-text-muted">
        {SHARE_DESCRIPTION}
      </p>
      {share.data ? (
        <LinkManagement
          bundle={bundle}
          proposalId={proposalId}
          state={share.data}
        />
      ) : share.isError ? (
        <p role="alert" className="text-[length:var(--step--1)] text-bp-danger">
          {SHARE_LOAD_ERROR_PROMPT}
        </p>
      ) : (
        <p
          role="status"
          className="text-[length:var(--step--1)] text-bp-text-muted"
        >
          {LOADING_SHARE_LABEL}
        </p>
      )}
      {ExtraActions ? <ExtraActions proposalId={proposalId} /> : null}
      <ActivityList
        views={views.data?.views ?? []}
        isLoading={views.isLoading}
        isError={views.isError}
      />
    </div>
  )
}

function LinkManagement<Readiness extends ShareReadinessDto>({
  bundle,
  proposalId,
  state,
}: {
  bundle: ShareKindBundle<Readiness>
  proposalId: string
  state: ShareStateDto<Readiness>
}): JSX.Element {
  // A proposal tree may not mount useAuth, so the cached profile can be
  // absent. Hide a control only for a viewer whose permissions are known and
  // insufficient; the server stays the authority either way.
  const profile = useCurrentUser().data
  const { updatePermissions } = bundle
  const canManage =
    profile == null ||
    updatePermissions == null ||
    updatePermissions.some((permission) =>
      profile.permissions.includes(permission),
    )

  return state.grant.state === `published` ? (
    <PublishedLink
      bundle={bundle}
      proposalId={proposalId}
      canManage={canManage}
      readiness={state.readiness}
      url={state.grant.url}
      expiresAt={state.grant.expiresAt}
    />
  ) : (
    <PublishAction
      bundle={bundle}
      proposalId={proposalId}
      canManage={canManage}
      readiness={state.readiness}
    />
  )
}

const notReady = <Readiness extends ShareReadinessDto>(
  readiness: Readiness,
): NotReadyReadiness<Readiness> | null =>
  readiness.ready ? null : (readiness as NotReadyReadiness<Readiness>)

function NotReadyNotice<Readiness extends ShareReadinessDto>({
  bundle,
  proposalId,
  readiness,
  published,
}: {
  bundle: ShareKindBundle<Readiness>
  proposalId: string
  readiness: NotReadyReadiness<Readiness>
  published: boolean
}): JSX.Element {
  const { NotReady } = bundle

  return (
    <div
      data-testid={`${bundle.testIdPrefix}-share-not-ready`}
      className="flex flex-col items-start gap-3"
    >
      <NotReady
        proposalId={proposalId}
        readiness={readiness}
        published={published}
      />
    </div>
  )
}

function PublishAction<Readiness extends ShareReadinessDto>({
  bundle,
  proposalId,
  canManage,
  readiness,
}: {
  bundle: ShareKindBundle<Readiness>
  proposalId: string
  canManage: boolean
  readiness: Readiness
}): JSX.Element {
  const publish = bundle.usePublish()
  // A publish that loses a race to a pricing change gets the same reason the
  // readiness read would have given, so the refusal explains itself.
  const blocked = bundle.blockedReadiness(publish.error)
  const shown = notReady(readiness) ?? blocked
  const canPublish = canManage && readiness.ready && blocked == null

  return (
    <section
      aria-label={PUBLIC_LINK_LABEL}
      className="flex flex-col gap-3 rounded-lg border border-bp-border bg-bp-elevated px-4 py-4"
    >
      {shown ? (
        <NotReadyNotice
          bundle={bundle}
          proposalId={proposalId}
          readiness={shown}
          published={false}
        />
      ) : null}
      {publish.isError && blocked == null ? (
        <p role="alert" className="text-[length:var(--step--2)] text-bp-danger">
          {PUBLISH_ERROR_PROMPT}
        </p>
      ) : null}
      {canManage ? (
        <button
          type="button"
          disabled={!canPublish || publish.isLoading}
          onClick={() => publish.mutate(proposalId)}
          className={clsx(
            `w-fit rounded-md px-5 py-2.5 text-[length:var(--step--1)] font-semibold`,
            canPublish && !publish.isLoading
              ? `bg-bp-accent text-bp-on-accent hover:bg-bp-accent-hover`
              : `cursor-not-allowed border border-bp-border-strong bg-bp-elevated-2 text-bp-text-dim`,
          )}
        >
          {publish.isLoading ? PUBLISHING_LABEL : PUBLISH_LABEL}
        </button>
      ) : (
        <p className="text-[length:var(--step--2)] text-bp-text-muted">
          {VIEW_ONLY_NOTE}
        </p>
      )}
    </section>
  )
}

function PublishedLink<Readiness extends ShareReadinessDto>({
  bundle,
  proposalId,
  canManage,
  readiness,
  url,
  expiresAt,
}: {
  bundle: ShareKindBundle<Readiness>
  proposalId: string
  canManage: boolean
  readiness: Readiness
  url: string
  expiresAt: string
}): JSX.Element {
  const unpublish = bundle.useUnpublish()
  // Reset on a new token so a copy of the revoked link never reads as copied.
  const [copyState, setCopyState] = useState<`copied` | `failed` | `idle`>(
    `idle`,
  )
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null)
  const copied = copyState === `copied` && copiedUrl === url
  const expiration = formatExpiration(expiresAt)
  const stale = notReady(readiness)

  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedUrl(url)
      setCopyState(`copied`)
    } catch {
      setCopiedUrl(null)
      setCopyState(`failed`)
    }
  }

  return (
    <section
      aria-label={PUBLIC_LINK_LABEL}
      className="flex flex-col gap-3 rounded-lg border border-bp-border bg-bp-elevated px-4 py-4"
    >
      <label className="flex flex-col gap-2 text-[length:var(--step--2)] font-semibold text-bp-text-muted">
        {PUBLIC_LINK_LABEL}
        <input
          readOnly
          value={url}
          onFocus={(event) => event.target.select()}
          className="w-full rounded-md border border-bp-border-strong bg-bp-elevated-2 px-3 py-2 text-[length:var(--step--1)] font-normal text-bp-text"
        />
      </label>
      {expiration ? (
        <p className="text-[length:var(--step--2)] text-bp-text-dim">
          {`Expires ${expiration}`}
        </p>
      ) : null}
      {/* An active link keeps serving the presentation while its money is
          stale, with the amounts withheld. The seller has to be told that. */}
      {stale ? (
        <NotReadyNotice
          bundle={bundle}
          proposalId={proposalId}
          readiness={stale}
          published
        />
      ) : null}
      {copyState === `failed` ? (
        <p role="alert" className="text-[length:var(--step--2)] text-bp-danger">
          {COPY_ERROR_PROMPT}
        </p>
      ) : null}
      {unpublish.isError ? (
        <p role="alert" className="text-[length:var(--step--2)] text-bp-danger">
          {UNPUBLISH_ERROR_PROMPT}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void onCopy()}
          className="rounded-md bg-bp-accent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-on-accent hover:bg-bp-accent-hover"
        >
          {copied ? LINK_COPIED_LABEL : COPY_LINK_LABEL}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-md border border-bp-border-strong px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-text hover:border-bp-accent"
        >
          {OPEN_PRESENTATION_LABEL}
        </a>
        {canManage ? (
          <button
            type="button"
            disabled={unpublish.isLoading}
            onClick={() => unpublish.mutate(proposalId)}
            className="rounded-md px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-danger hover:text-bp-text disabled:cursor-not-allowed disabled:opacity-50"
          >
            {unpublish.isLoading ? UNPUBLISHING_LABEL : UNPUBLISH_LABEL}
          </button>
        ) : null}
      </div>
    </section>
  )
}
